import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService, { ListResult } from "../../services/ApiService";
import {
  CreateTeacherInput,
  TeacherData,
  UpdateTeacherInput,
} from "../../model/TeacherData";

interface TeachersState {
  teachers: TeacherData[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: TeachersState = {
  teachers: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
  isLoading: false,
  error: null,
};

export const fetchTeachers = createAsyncThunk<
  ListResult<TeacherData>,
  { page: number; pageSize: number; searchKey?: string },
  { rejectValue: string }
>("teachers/fetchTeachers", async (params, { rejectWithValue }) => {
  const res = await apiService.getTeachers({
    take: params.pageSize,
    skip: (params.page - 1) * params.pageSize,
    search: params.searchKey,
  });
  if (res.success && res.data) return res.data;
  return rejectWithValue(res.message || "Failed to fetch teachers");
});

export const createTeacher = createAsyncThunk<
  TeacherData,
  CreateTeacherInput,
  { rejectValue: string }
>("teachers/createTeacher", async (data, { rejectWithValue }) => {
  const res = await apiService.createTeacher(data);
  if (res.success && res.data) return res.data;
  return rejectWithValue(res.message || "Failed to create teacher");
});

export const updateTeacher = createAsyncThunk<
  TeacherData,
  { id: string; data: UpdateTeacherInput },
  { rejectValue: string }
>("teachers/updateTeacher", async ({ id, data }, { rejectWithValue }) => {
  const res = await apiService.updateTeacher(id, data);
  if (res.success && res.data) return res.data;
  return rejectWithValue(res.message || "Failed to update teacher");
});

export const deleteTeacher = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("teachers/deleteTeacher", async (id, { rejectWithValue }) => {
  const res = await apiService.deleteTeacher(id);
  if (res.success) return id;
  return rejectWithValue(res.message || "Failed to delete teacher");
});

const teachersSlice = createSlice({
  name: "teachers",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeachers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTeachers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.teachers = action.payload.data;
        state.totalCount = action.payload.total;
      })
      .addCase(fetchTeachers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to fetch teachers";
      })
      .addCase(createTeacher.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to create teacher";
      })
      .addCase(updateTeacher.fulfilled, (state, action) => {
        const idx = state.teachers.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.teachers[idx] = action.payload;
      })
      .addCase(updateTeacher.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to update teacher";
      })
      .addCase(deleteTeacher.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to delete teacher";
      });
  },
});

export const { clearError, setPage, setPageSize } = teachersSlice.actions;

type Root = { teachers: TeachersState };
export const selectTeachers = (state: Root) => state.teachers.teachers;
export const selectTeachersTotal = (state: Root) => state.teachers.totalCount;
export const selectTeachersPage = (state: Root) => state.teachers.currentPage;
export const selectTeachersPageSize = (state: Root) => state.teachers.pageSize;
export const selectTeachersLoading = (state: Root) => state.teachers.isLoading;
export const selectTeachersError = (state: Root) => state.teachers.error;

export default teachersSlice.reducer;
